/**
 * FAQ block - single-open accordion behavior.
 * Only one item can be open at a time. First item open by default.
 */
class FaqCustom extends HTMLElement {
  get #openIndex() {
    return parseInt(this.dataset.openIndex ?? '0', 10);
  }

  set #openIndex(value) {
    this.dataset.openIndex = String(value);
  }

  get #items() {
    return Array.from(this.querySelectorAll('.faq-item'));
  }

  get #triggers() {
    return Array.from(this.querySelectorAll('[data-faq-trigger]'));
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;
    const items = this.#items;
    const triggers = this.#triggers;

    this.#setOpenState(this.#openIndex);

    triggers.forEach((trigger, idx) => {
      trigger.addEventListener(
        'click',
        () => {
          const newIndex = this.#openIndex === idx ? -1 : idx;
          this.#openIndex = newIndex;
          this.#setOpenState(newIndex);
        },
        { signal }
      );
    });

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /** @param {number} openIndex */
  #setOpenState(openIndex) {
    const items = this.#items;
    const triggers = this.#triggers;

    items.forEach((item, idx) => {
      const isOpen = idx === openIndex;
      if (item instanceof HTMLElement) {
        item.dataset.open = isOpen ? 'true' : 'false';
      }
      const trigger = triggers[idx];
      if (trigger) {
        trigger.setAttribute('aria-expanded', String(isOpen));
      }
    });
  }

  /** @param {KeyboardEvent} event */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    const openIdx = this.#openIndex;
    if (openIdx < 0) return;
    event.preventDefault();
    this.#openIndex = -1;
    this.#setOpenState(-1);
    const trigger = this.#triggers[openIdx];
    if (trigger instanceof HTMLElement) {
      trigger.focus();
    }
  }
}

if (!customElements.get('faq-custom')) {
  customElements.define('faq-custom', FaqCustom);
}
